import { useEffect, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, useFormState, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useIntake } from '../useIntake';
import { useStore } from '../../store';
import { derivePushMode } from '../../components/intake/pushMode';
import {
  createEpisodeStepFormSchemaWithOptions,
  type CreateEpisodeStepFormData,
} from '../../schemas/createEpisodeStepFormSchema';
import { getReferralDocs } from '../../services/api';

import { useFormAutoSave } from './useFormAutoSave';
import { useAddressValidation } from './useAddressValidation';
import { useClinicData } from './useClinicData';
import { useKinnserCreation } from './useKinnserCreation';
import { useKinnserOnboarding, type S3File } from './useKinnserOnboarding';
import { useRogerCreation } from './useRogerCreation';
import { usePhysicianOptions } from './usePhysicianOptions';
import { useInternalReferralSourceOptions } from './useInternalReferralSourceOptions';
import { useFormInitialization } from './useFormInitialization';
import { CREATE_EPISODE_STEP_DEFAULT_VALUES } from './constants';
import type { DisplayValues } from './types';

export function useCreateEpisodeStepForm() {
  const { intakeId } = useParams<{ intakeId: string }>();
  const { selectedIntake, updateIntakeRecord } = useIntake();

  // When visit type is ROC (schedule task on existing episode), skip Kinnser create-patient validation
  const skipKinnserValidation = selectedIntake?.kinnser_visit_type === 'roc';
  const user = useStore(state => state.user);
  const hasPermission = useStore(state => state.hasPermission);
  const hasKinnserOption = user?.agency?.is_kinnser_enabled === true;

  // Ref updated when clinic data loads so resolver can require MRN when RogerDoc auto_mrn is off
  const autoMrnRef = useRef<boolean | undefined>(undefined);
  // REC (recert onboarding): explicit Original MRN required even on auto-MRN clinics.
  const requireExplicitMrnRef = useRef<boolean>(false);

  const resolver: Resolver<CreateEpisodeStepFormData> = useCallback(
    (values, context, options) => {
      const schema = createEpisodeStepFormSchemaWithOptions({
        autoMrn: autoMrnRef.current,
        requireExplicitMrn: requireExplicitMrnRef.current,
      });
      return zodResolver(schema)(values, context, options);
    },
    []
  );

  // React Hook Form setup
  const {
    control,
    register,
    reset,
    watch,
    setValue,
    trigger,
    clearErrors,
    getValues,
    handleSubmit,
  } = useForm<CreateEpisodeStepFormData>({
    resolver,
    defaultValues: CREATE_EPISODE_STEP_DEFAULT_VALUES,
    mode: 'onChange',
  });

  // Subscribe to form state changes (errors, isValid) separately for proper reactivity
  const { errors, dirtyFields, isValid } = useFormState({ control });

  // Watch form values for cascading logic (dropdowns, etc.)
  const agencyClinicId = watch('agency_clinic_id');
  const hhCahpsOptOut = watch('kinnser_hh_cahps_opt_out');
  const kinnserLanguageKey = watch('kinnser_language_key');
  const pushToRoger = watch('push_to_roger');
  const pushToKinnser = watch('push_to_kinnser');

  // Validate Other Language field when Language dropdown changes
  useEffect(() => {
    if (kinnserLanguageKey === '6') {
      // When "Other/Unknown" is selected, trigger validation on the other language field
      trigger('kinnser_other_language');
    } else {
      // When any other language is selected, clear any validation errors on the other language field
      clearErrors('kinnser_other_language');
    }
  }, [kinnserLanguageKey, trigger, clearErrors]);

  // Auto-save hook - saves on blur, then resets baseline
  const { saveStatus, saveToServer, handleFieldBlur } = useFormAutoSave({
    intakeId,
    selectedIntake,
    updateIntakeRecord,
    control,
    getValues,
    trigger,
    reset,
  });

  // Address validation hook
  const addressValidation = useAddressValidation({
    setValue,
    getValues,
    saveToServer,
  });

  const {
    clinics,
    clinicData,
    loadingClinics,
    loadingClinicData,
    insuranceOptions,
  } = useClinicData({
    agencySystemName: user?.agency?.system_name,
    agencyClinicId,
    hasKinnserOption,
    pushToKinnser,
    pushToRoger,
    selectedIntake,
    setValue,
    trigger,
    intakeId,
    saveToServer,
  });

  // Keep ref in sync so resolver uses current auto_mrn for MRN requirement (Roger + Kinnser)
  autoMrnRef.current = clinicData?.auto_mrn;
  // REC push: keep the original MRN explicit. Derived through derivePushMode —
  // the same function CreateEpisodeStep uses for requireExplicitMrn display —
  // so the resolver and the rendered form can never disagree (a stale 'rec'
  // with a Kinnser push must not demand an MRN the Kinnser form hides).
  requireExplicitMrnRef.current =
    derivePushMode({
      isNewPatient: selectedIntake?.is_new_patient ?? null,
      visitType: selectedIntake?.kinnser_visit_type ?? null,
      creationType: selectedIntake?.kinnser_creation_type ?? null,
      pushToKinnser: selectedIntake?.push_to_kinnser === true,
      pushToRoger: selectedIntake?.push_to_roger === true,
    }).kind === 'rec';

  // Physician options (ClinicPhysician — episode.clinic_physician_id / intake.clinic_physician_id).
  const { rogerPhysicianOptions, loadingPhysicians } =
    usePhysicianOptions(agencyClinicId);

  // Internal referral source options (RogerEMR push only; copied onto the
  // admission the push creates).
  const { internalReferralSourceOptions, loadingInternalReferralSources } =
    useInternalReferralSourceOptions(agencyClinicId);

  // State for S3 files of intake documents (needed for onboarding)
  const [s3Files, setS3Files] = useState<S3File[]>([]);

  // Check if episode creation is enabled (from Scheduling step)
  const shouldCreateEpisode: boolean = !!(
    selectedIntake?.kinnser_create_episode ||
    selectedIntake?.roger_create_episode
  );
  const isNewPatient =
    (selectedIntake as { is_new_patient?: boolean | null })?.is_new_patient ===
    true;
  // New admission + episode and new episode use auto_add flow (in CreateEpisodeStep), not onboarding — unless patient is new (then always onboard).
  const creationType = (
    selectedIntake as { kinnser_creation_type?: string | null }
  )?.kinnser_creation_type;
  const useAutoAddFlow =
    !isNewPatient &&
    (creationType === 'new_admission' || creationType === 'new_episode');

  // Kinnser onboarding hook (used when episode creation + doc upload, and NOT using auto_add flow; or when patient is new we always use onboarding)
  const onboardingHook = useKinnserOnboarding<CreateEpisodeStepFormData>({
    intakeId,
    intakeStatus: selectedIntake?.status,
    kinnserOnboardWorkflowId: selectedIntake?.kinnser_onboard_workflow_id,
    trigger,
    getValues,
    tabName: user?.agency?.hnp_upload_tab ?? null,
    taskName: user?.agency?.hnp_upload_task ?? null,
    s3Files: s3Files,
  });

  // Kinnser creation hook (used when episode creation is disabled or feature flag is false)
  const creationHook = useKinnserCreation({
    intakeId,
    intakeStatus: selectedIntake?.status,
    trigger,
    skipValidation: skipKinnserValidation,
  });

  // Roger creation hook
  const rogerCreationHook = useRogerCreation({
    intakeId,
    intakeStatus: selectedIntake?.status,
    trigger,
  });

  // Normalize return values based on which hook should be active for kinnser.
  // New patients always use onboarding. Otherwise when creation type is new_admission or new_episode, the step uses auto_add flow instead of onboarding.
  const shouldUseOnboarding =
    shouldCreateEpisode && (isNewPatient || !useAutoAddFlow);
  const handleCreateInKinnser = shouldUseOnboarding
    ? onboardingHook.handleOnboardInKinnser
    : creationHook.handleCreateInKinnser;

  // Combine creating in EMR states for kinnser and roger
  const creatingInKinnser = shouldUseOnboarding
    ? onboardingHook.onboardingInKinnser
    : creationHook.creatingInKinnser;
  const creatingInRoger = rogerCreationHook.creatingInRoger;
  const creatingInEMR: boolean = !!(
    (pushToRoger && creatingInRoger) ||
    (pushToKinnser && creatingInKinnser)
  );

  // Combine create results for kinnser and roger
  const kinnserCreateResult = shouldUseOnboarding
    ? onboardingHook.onboardResult
    : creationHook.createResult;
  const rogerCreateResult = rogerCreationHook.rogerCreateResult;
  const createResult = pushToRoger
    ? rogerCreateResult
    : pushToKinnser
      ? kinnserCreateResult
      : null;

  // Form initialization hook
  useFormInitialization({
    selectedIntake,
    reset,
    setValue,
    trigger,
    saveToServer,
  });

  // Display values derived from watched form values
  const watchedDob = watch('patient_dob');
  const intakeDob = selectedIntake?.patient_dob;
  const dobValue = watchedDob || intakeDob || '';

  const displayValues: DisplayValues = {
    firstName:
      watch('patient_first_name') || selectedIntake?.patient_first_name || '',
    lastName:
      watch('patient_last_name') || selectedIntake?.patient_last_name || '',
    dob: typeof dobValue === 'string' ? dobValue : '',
    phone: watch('patient_phone') || selectedIntake?.patient_phone || '',
    notes: watch('patient_notes') || selectedIntake?.patient_notes || '',
  };

  // Specialized blur handler for address fields - triggers save and address validation
  const handleAddressFieldBlur = useCallback(
    (fieldNameOrEvent: React.FocusEvent<HTMLElement>) => {
      if (!dirtyFields.patient_street_address && !dirtyFields.patient_zip_code)
        return;

      handleFieldBlur(fieldNameOrEvent);

      // Defer the validation check to allow the blur to complete
      setTimeout(() => {
        const values = getValues();
        if (
          values.patient_street_address &&
          values.patient_zip_code &&
          !values.push_to_roger
        ) {
          addressValidation.checkAddressValidation(
            values.patient_street_address,
            values.patient_zip_code
          );
        }
      }, 0);
    },
    [
      handleFieldBlur,
      getValues,
      addressValidation,
      dirtyFields.patient_street_address,
      dirtyFields.patient_zip_code,
    ]
  );

  // Specialized blur handler for clinic field - resets dependent fields
  const handleClinicFieldBlur = useCallback(() => {
    if (!dirtyFields.agency_clinic_id) return;

    // Reset dependent fields in form
    setValue('kinnser_physician_id', null);
    setValue('clinic_physician_id', null);
    setValue('kinnser_branch_id', null);
    setValue('kinnser_insurance_provider_id', null);
    // The referral option lists are scraped per clinic, so a value picked for
    // the previous clinic is not selectable under the new one.
    setValue('kinnser_internal_referral_id', null);
    setValue('kinnser_external_referral_id', null);
    setValue('kinnser_external_referral_name', null);

    const selectedClinicId = getValues('agency_clinic_id');

    saveToServer({
      agency_clinic_id: selectedClinicId,
      kinnser_physician_id: '',
      clinic_physician_id: null,
      kinnser_branch_id: '',
      kinnser_insurance_provider_id: '',
      kinnser_internal_referral_id: '',
      kinnser_external_referral_id: '',
      kinnser_external_referral_name: '',
    } as Partial<CreateEpisodeStepFormData>);
  }, [getValues, setValue, saveToServer, dirtyFields.agency_clinic_id]);

  const { resetValidation } = addressValidation;

  // Reset address validation when pushing to Roger
  useEffect(() => {
    if (pushToRoger) {
      resetValidation();
    }
  }, [pushToRoger, resetValidation]);

  // Fetch S3 URLs for all intake documents (only needed when onboarding is enabled)
  useEffect(() => {
    if (!shouldUseOnboarding) return;

    const fetchS3Files = async () => {
      if (!intakeId) return;

      try {
        const response = await getReferralDocs({
          path: { intake_id: intakeId },
        });

        const documents = response.data?.documents;
        if (documents && Array.isArray(documents) && documents.length > 0) {
          const files: S3File[] = (
            documents as unknown as Array<{
              url: string;
              file_name: string;
            }>
          ).map(doc => ({
            url: doc.url,
            filename: doc.file_name,
          }));
          setS3Files(files);
        }
      } catch (error) {
        console.error('Error fetching S3 URLs:', error);
        // Don't set error state - just log it, s3Files will remain empty
        // and useKinnserOnboarding will handle the validation
      }
    };

    fetchS3Files();
  }, [intakeId, shouldUseOnboarding]);

  return {
    // Route params
    intakeId,

    // Intake data
    selectedIntake,

    // Permissions
    hasPermission,

    // Form
    control,
    register,
    errors,
    setValue,
    getValues,
    handleSubmit,
    handleFieldBlur,

    // Watched values
    hhCahpsOptOut,
    kinnserLanguageKey,
    pushToRoger,
    pushToKinnser,

    // Display values
    displayValues,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics
    clinics,
    clinicData,
    loadingClinics,
    loadingClinicData,
    insuranceOptions,

    // Physician options (ClinicPhysician — episode.clinic_physician_id / intake.clinic_physician_id)
    rogerPhysicianOptions,
    loadingPhysicians,

    // Internal referral source options (RogerEMR push only)
    internalReferralSourceOptions,
    loadingInternalReferralSources,

    // Form validation
    isValid,

    // Create Episode Generic fields
    creatingInEMR,
    createResult,

    // Kinnser creation/onboarding
    handleCreateInKinnser,
    isUsingOnboarding: shouldUseOnboarding,

    // Roger creation
    handleCreateInRoger: rogerCreationHook.handleCreateInRoger,
    rogerCreateResult,

    // Address validation
    addressSuggestion: addressValidation.addressSuggestion,
    addressModalOpen: addressValidation.addressModalOpen,
    setAddressModalOpen: addressValidation.setAddressModalOpen,
    isValidatingAddress: addressValidation.isValidatingAddress,
    handleAddressFieldBlur,
    handleAcceptAddressSuggestion:
      addressValidation.handleAcceptAddressSuggestion,

    // Clinic field handler
    handleClinicFieldBlur,
  };
}
